import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  IoClose, 
  IoCheckmarkCircle, 
  IoTimeOutline,
  IoPersonOutline,
  IoMedkitOutline,
  IoDocumentTextOutline,
  IoCalendarOutline,
  IoLocationOutline,
  IoPencilOutline,
  IoSaveOutline
} from 'react-icons/io5';
import { 
  PROPACK_PRIMARY_OPTIONS, 
  PROPACK_SIZE_OPTIONS,
  PROPACK_SECONDARY_OPTIONS_MAPPING 
} from '../../../data/conservativeCareDashboard';
const API_BASE_URL = "https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/";

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const OrderDetailsModal = ({ open, onClose, orderId }) => {
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && orderId) {
      fetchOrderDetails();
    }
  }, [open, orderId]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/carekit/${orderId}/`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch order details');
      }
      
      const data = await response.json();
      setOrderDetails(data);
      setEditData({
        facility_name: data.facility_name,
        phone_number: data.phone_number,
        street: data.street,
        city: data.city,
        zip_code: data.zip_code,
        country: data.country,
        wound_type: data.wound_type,
        wound_location: data.wound_location,
        wound_length: data.wound_length,
        wound_width: data.wound_width,
        wound_depth: data.wound_depth,
        is_chronic_wound: data.is_chronic_wound,
        is_full_thickness: data.is_full_thickness,
        wound_drainage: data.wound_drainage,
        icd10_code: data.icd10_code,
        kit_duration: data.kit_duration,
        conservative_care: data.conservative_care,
        tracking_number: data.tracking_number,
        propack_config: data.propack_config,  
        propack_primary: data.propack_config?.primary?.type || '',
    propack_primary_size: data.propack_config?.primary?.size || '',
    propack_secondary: data.propack_config?.secondary?.type || '',
    propack_secondary_size: data.propack_config?.secondary?.size || '',
    graft_name: data.graft_name,
    graft_size: data.graft_size,
      });
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSecondaryOptions = () => {
  if (!editData.kit_duration || !editData.wound_drainage || !editData.propack_primary_size) {
    return [];
  }
  
  // Capitalize first letter of wound_drainage to match mapping keys
  const capitalizedDrainage = editData.wound_drainage.charAt(0).toUpperCase() + editData.wound_drainage.slice(1);
  
  return PROPACK_SECONDARY_OPTIONS_MAPPING[editData.kit_duration]?.[capitalizedDrainage]?.[editData.propack_primary_size] || [];
};

  // Add this useEffect after the getSecondaryOptions function
useEffect(() => {
  if (editMode) {
    const secondaryOpts = getSecondaryOptions();
    
    // Auto-fill secondary if there's a stored value that matches
    if (editData.propack_secondary && secondaryOpts.length > 0) {
      const matchingOpt = secondaryOpts.find(opt => 
        opt.value === editData.propack_secondary || 
        opt.productSize === editData.propack_secondary_size
      );
      
      if (matchingOpt) {
        setEditData(prev => ({
          ...prev,
          propack_secondary: matchingOpt.value,
          propack_secondary_size: matchingOpt.productSize
        }));
      }
    }
  }
}, [editMode, editData.kit_duration, editData.wound_drainage, editData.propack_primary_size]);

  const handleSave = async () => {
  setSaving(true);
  try {
    const saveData = { ...editData };
    
    // Build propack_config from individual fields
    if (orderDetails.care_type === 'propacks') {
      saveData.propack_config = {
        primary: {
          type: editData.propack_primary,
          size: editData.propack_primary_size
        },
        secondary: {
          type: editData.propack_secondary,
          size: editData.propack_secondary_size
        }
      };
      // Remove individual fields
      delete saveData.propack_primary;
      delete saveData.propack_primary_size;
      delete saveData.propack_secondary;
      delete saveData.propack_secondary_size;
    }
    
    const response = await fetch(`${API_BASE_URL}/${orderId}/update/`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(saveData),
    });

    if (!response.ok) {
      throw new Error('Failed to update order');
    }

    const data = await response.json();
    setOrderDetails(data.data);
    setEditMode(false);
  } catch (err) {
    alert('Failed to update order: ' + err.message);
  } finally {
    setSaving(false);
  }
};

  if (!open) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div 
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
          initial={{ scale: 0.9, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 50 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-500 to-cyan-600 p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-black text-white">Order Details</h2>
              <p className="text-teal-100 text-sm">View complete order information</p>
            </div>
            <div className="flex gap-2">
              {!editMode && orderDetails && (
                <motion.button
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full flex items-center gap-2 text-white transition-all font-semibold"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <IoPencilOutline className="text-lg" />
                  Edit
                </motion.button>
              )}
              <motion.button
                onClick={onClose}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-all"
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                <IoClose className="text-2xl" />
              </motion.button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="relative">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-500 border-t-transparent"></div>
                  <div className="absolute inset-0 animate-ping rounded-full h-16 w-16 border-4 border-teal-300 opacity-20"></div>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <div className="text-6xl mb-4">⚠️</div>
                <p className="text-red-600 dark:text-red-400 font-semibold">{error}</p>
                <button
                  onClick={fetchOrderDetails}
                  className="mt-4 px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-all"
                >
                  Retry
                </button>
              </div>
            ) : orderDetails ? (
              <div className="space-y-6">
                {editMode && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">✏️</div>
                      <div>
                        <p className="font-bold text-amber-900 dark:text-amber-200">Edit Mode Active</p>
                        <p className="text-sm text-amber-700 dark:text-amber-300">Make changes and click Save</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50"
                      >
                        <IoSaveOutline className="text-lg" />
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setEditMode(false);
                          setEditData({
                            facility_name: orderDetails.facility_name,
                            phone_number: orderDetails.phone_number,
                            street: orderDetails.street,
                            city: orderDetails.city,
                            zip_code: orderDetails.zip_code,
                            country: orderDetails.country,
                            wound_type: orderDetails.wound_type,
                            wound_location: orderDetails.wound_location,
                            wound_length: orderDetails.wound_length,
                            wound_width: orderDetails.wound_width,
                            wound_depth: orderDetails.wound_depth,
                            is_chronic_wound: orderDetails.is_chronic_wound,
                            is_full_thickness: orderDetails.is_full_thickness,
                            wound_drainage: orderDetails.wound_drainage,
                            icd10_code: orderDetails.icd10_code,
                            kit_duration: orderDetails.kit_duration,
                            conservative_care: orderDetails.conservative_care,
                            tracking_number: orderDetails.tracking_number,
                            propack_config: orderDetails.propack_config,  // ADD THIS
                            propack_primary: orderDetails.propack_config?.primary?.type || '',
                            propack_primary_size: orderDetails.propack_config?.primary?.size || '',
                            propack_secondary: orderDetails.propack_config?.secondary?.type || '',
                            propack_secondary_size: orderDetails.propack_config?.secondary?.size || '',
                            graft_name: orderDetails.graft_name,
                            graft_size: orderDetails.graft_size,
                            
                          });
                        }}
                        className="px-4 py-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-900 dark:text-white rounded-lg font-semibold"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Order Number & Status */}
                <motion.div 
                  className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border-2 border-teal-200 dark:border-teal-800 rounded-2xl p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <p className="text-sm font-bold text-gray-600 dark:text-gray-400 uppercase mb-1">Order Number</p>
                      <p className="text-2xl font-black text-gray-900 dark:text-white">{orderDetails.order_number}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-600 dark:text-gray-400 uppercase mb-2">Status</p>
                      <span className={`px-6 py-2 rounded-full text-sm font-bold shadow-lg ${
                        orderDetails.status === 'pending' ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900' :
                        orderDetails.status === 'processing' ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-blue-900' :
                        orderDetails.status === 'shipped' ? 'bg-gradient-to-r from-purple-400 to-purple-500 text-purple-900' :
                        orderDetails.status === 'delivered' ? 'bg-gradient-to-r from-green-400 to-green-500 text-green-900' :
                        'bg-gradient-to-r from-gray-400 to-gray-500 text-gray-900'
                      }`}>
                        {orderDetails.status_display}
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Patient Information */}
                <motion.div 
                  className="bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-2xl p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                      <IoPersonOutline className="text-2xl text-white" />
                    </div>
                    <h3 className="text-xl font-black text-gray-900 dark:text-white">Patient Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Patient Name</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">{orderDetails.patient_name}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Patient ID</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">#{orderDetails.patient}</p>
                    </div>
                  </div>
                </motion.div>

                {/* Wound Details */}
                <motion.div 
                  className="bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-2xl p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                      <IoMedkitOutline className="text-2xl text-white" />
                    </div>
                    <h3 className="text-xl font-black text-gray-900 dark:text-white">Wound Details</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Wound Type</p>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData.wound_type}
                          onChange={(e) => setEditData({ ...editData, wound_type: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.wound_type || 'N/A'}</p>
                      )}
                    </div>
                    
                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Location</p>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData.wound_location}
                          onChange={(e) => setEditData({ ...editData, wound_location: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.wound_location || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Length (cm)</p>
                      {editMode ? (
                        <input
                          type="number"
                          step="0.1"
                          value={editData.wound_length}
                          onChange={(e) => setEditData({ ...editData, wound_length: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.wound_length || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Width (cm)</p>
                      {editMode ? (
                        <input
                          type="number"
                          step="0.1"
                          value={editData.wound_width}
                          onChange={(e) => setEditData({ ...editData, wound_width: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.wound_width || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Depth (cm)</p>
                      {editMode ? (
                        <input
                          type="number"
                          step="0.1"
                          value={editData.wound_depth}
                          onChange={(e) => setEditData({ ...editData, wound_depth: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.wound_depth || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Drainage / Exudate</p>
                      {editMode ? (
                        <select
                          value={editData.wound_drainage}
                          onChange={(e) => setEditData({ ...editData, wound_drainage: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Select drainage</option>
                          <option value="mild">Mild</option>
                          <option value="moderate">Moderate</option>
                        </select>
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white capitalize">{orderDetails.wound_drainage_display || 'N/A'}</p>
                      )}
                    </div>

                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Chronic Wound</p>
                      {editMode ? (
                        <select
                          value={editData.is_chronic_wound}
                          onChange={(e) => setEditData({ ...editData, is_chronic_wound: e.target.value === 'true' })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </select>
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.is_chronic_wound ? 'Yes' : 'No'}</p>
                      )}
                    </div>

                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Full Thickness</p>
                      {editMode ? (
                        <select
                          value={editData.is_full_thickness}
                          onChange={(e) => setEditData({ ...editData, is_full_thickness: e.target.value === 'true' })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </select>
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.is_full_thickness ? 'Yes' : 'No'}</p>
                      )}
                    </div>

                    <div className="col-span-2">
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">ICD-10 Code</p>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData.icd10_code}
                          onChange={(e) => setEditData({ ...editData, icd10_code: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.icd10_code || 'N/A'}</p>
                      )}
                    </div>
                  </div>
                </motion.div>

                {/* Kit/Care Information */}
                <motion.div 
                  className="bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-2xl p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-2xl">
                      📦
                    </div>
                    <h3 className="text-xl font-black text-gray-900 dark:text-white">Care Package Details</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Care Type</p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.care_type_display}</p>
                    </div>

                    {orderDetails.kit_duration && (
                      <div>
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Duration</p>
                        {editMode ? (
                          <select
                            value={editData.kit_duration}
                            onChange={(e) => setEditData({ ...editData, kit_duration: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          >
                            <option value="12-day">12 Days</option>
                            <option value="30-day">30 Days</option>
                            
                          </select>
                        ) : (
                          <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.kit_duration_display}</p>
                        )}
                      </div>
                    )}

                    {orderDetails.care_type === 'propacks' && (
  <div className="col-span-2">
    <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">ProPack Configuration</p>
    {editMode ? (
      <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 block">Primary Type</label>
            <select
              value={editData.propack_primary}
              onChange={(e) => {
                setEditData({
                  ...editData,
                  propack_primary: e.target.value,
                  propack_primary_size: '',
                  propack_secondary: '',
                  propack_secondary_size: ''
                });
              }}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="">Select...</option>
              {PROPACK_PRIMARY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 block">Primary Size</label>
            <select
              value={editData.propack_primary_size}
              onChange={(e) => {
                setEditData({
                  ...editData,
                  propack_primary_size: e.target.value,
                  propack_secondary: '',
                  propack_secondary_size: ''
                });
              }}
              disabled={!editData.propack_primary}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm disabled:opacity-50"
            >
              <option value="">Select size...</option>
              {PROPACK_SIZE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 block">Secondary Dressing</label>
          <select
            value={editData.propack_secondary}
            onChange={(e) => {
              const selectedOpt = getSecondaryOptions().find(opt => opt.value === e.target.value);
              setEditData({
                ...editData,
                propack_secondary: e.target.value,
                propack_secondary_size: selectedOpt?.productSize || ''
              });
            }}
            disabled={getSecondaryOptions().length === 0}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm disabled:opacity-50"
          >
            <option value="">
              {getSecondaryOptions().length === 0 
                ? 'Select kit duration, exudate, and primary size first'
                : 'Select secondary dressing...'}
            </option>
            {getSecondaryOptions().map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {getSecondaryOptions().length === 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              ⚠️ Complete kit duration, exudate level, and primary size to see options
            </p>
          )}
        </div>
      </div>
    ) : (
      <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.propack_display}</p>
    )}
  </div>
)}

{orderDetails.care_type === 'advanced' && (
  <div className="col-span-2">
    <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Graft Details</p>
    {editMode ? (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <input
            type="text"
            value={editData.graft_name || ''}
            onChange={(e) => setEditData({ ...editData, graft_name: e.target.value })}
            placeholder="Graft Name"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <input
            type="text"
            value={editData.graft_size || ''}
            onChange={(e) => setEditData({ ...editData, graft_size: e.target.value })}
            placeholder="Size (e.g., 5x5)"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
      </div>
    ) : (
      <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.graft_display}</p>
    )}
  </div>
)}


                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Conservative Care</p>
                      {editMode ? (
                        <select
                          value={editData.conservative_care}
                          onChange={(e) => setEditData({ ...editData, conservative_care: e.target.value === 'true' })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </select>
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.conservative_care ? 'Yes' : 'No'}</p>
                      )}
                    </div>
                  </div>
                </motion.div>

                {/* Facility & Delivery Information */}
                <motion.div 
                  className="bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-2xl p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                      <IoLocationOutline className="text-2xl text-white" />
                    </div>
                    <h3 className="text-xl font-black text-gray-900 dark:text-white">Delivery Information</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Facility</p>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData.facility_name}
                          onChange={(e) => setEditData({ ...editData, facility_name: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.facility_name}</p>
                      )}
                    </div>
                    
                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Address</p>
                      {editMode ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder="Street"
                            value={editData.street}
                            onChange={(e) => setEditData({ ...editData, street: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                          <div className="grid grid-cols-3 gap-2">
                            <input
                              type="text"
                              placeholder="City"
                              value={editData.city}
                              onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                            <input
                              type="text"
                              placeholder="Zip"
                              value={editData.zip_code}
                              onChange={(e) => setEditData({ ...editData, zip_code: e.target.value })}
                              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                            <input
                              type="text"
                              placeholder="Country"
                              value={editData.country}
                              onChange={(e) => setEditData({ ...editData, country: e.target.value })}
                              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                          </div>
                        </div>
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white">
                          {orderDetails.street}, {orderDetails.city}, {orderDetails.zip_code}, {orderDetails.country}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Phone</p>
                      {editMode ? (
                        <input
                          type="tel"
                          value={editData.phone_number}
                          onChange={(e) => setEditData({ ...editData, phone_number: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.phone_number}</p>
                      )}
                    </div>

                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Tracking Number</p>
                      {editMode ? (
                        <input
                          type="text"
                          value={editData.tracking_number || ''}
                          onChange={(e) => setEditData({ ...editData, tracking_number: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          placeholder="Enter tracking number"
                        />
                      ) : (
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{orderDetails.tracking_number || 'Not yet shipped'}</p>
                      )}
                    </div>
                  </div>
                </motion.div>

                {/* Timestamps */}
                <motion.div 
                  className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1 flex items-center gap-1">
                        <IoCalendarOutline /> Created
                      </p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {new Date(orderDetails.created_at).toLocaleString()}
                      </p>
                    </div>
                    {orderDetails.shipped_at && (
                      <div>
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1 flex items-center gap-1">
                          <IoCalendarOutline /> Shipped
                        </p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {new Date(orderDetails.shipped_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                    {orderDetails.delivery_date && (
                      <div>
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1 flex items-center gap-1">
                          <IoCalendarOutline /> Expected Delivery
                        </p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {new Date(orderDetails.delivery_date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>
            ) : null}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default OrderDetailsModal;